import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPermissions } from '@ports/common/authStateSlice';
import { useDispatch } from 'react-redux';
import {
  setMemoryFilterOptions,
  setPhotoFilterOptions,
  setRecipeFilterOptions,
  setFolderFilterOptions,
  setMomentFilterOptions,
  setPeopleFilterOptions,
  setTrashFilterOptions,
  setSharingFilterOptions,
  getHaveSubscription
} from '@ports/common/sessionStateSlice';
import { routePaths } from '@utils/constants/routePaths';
import { Button } from '../Button';

interface ProfileSidebarItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path?: string;
  key: string;
  permission?: string;
  children?: { label: string; path: string; icon: any; selectedIcon?: any }[];
}

export const ProfileSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAnySettingsRoute = location.pathname.startsWith('/settings');
  const [openSettings, setOpenSettings] = useState<boolean>(isAnySettingsRoute);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const permission = useSelector(getPermissions);
  const isHaveSubscription = useSelector(getHaveSubscription);
  const isPermission = permission.includes('subscription_payment:false');
  useEffect(() => {
    if (isAnySettingsRoute) {
      setOpenSettings(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const defaultFilterOptions = {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  };

  const resetFilterOptionsForPath = () => {
    // Reset ALL filters when going to dashboard
    dispatch(setPhotoFilterOptions(defaultFilterOptions));
    dispatch(setMemoryFilterOptions(defaultFilterOptions));
    dispatch(setRecipeFilterOptions(defaultFilterOptions));
    dispatch(setPeopleFilterOptions(defaultFilterOptions));
    dispatch(setFolderFilterOptions(defaultFilterOptions));
    dispatch(setMomentFilterOptions(defaultFilterOptions));
    dispatch(setTrashFilterOptions(defaultFilterOptions));
    dispatch(setSharingFilterOptions(defaultFilterOptions));
  };

  const dashboardPath = permission.includes('owner_access:true')
    ? routePaths.Photos
    : routePaths.Sharing;

  const sidebarItems: ProfileSidebarItem[] = [
    {
      key: 'dashboard',
      icon: SVGAssets.DashboardIcon,
      label: t('goToDashboard'),
      path: dashboardPath
    },
    {
      key: 'myProfile',
      icon: SVGAssets.PersonIcon,
      label: t('myProfile'),
      path: '/my-profile'
    },
    {
      key: 'settings',
      icon: SVGAssets.SettingsIcon,
      label: t('accountSettings'),
      permission: 'accounts:view',
      children: [
        !isPermission && {
          label: t('subscriptions'),
          path: '/settings/subscriptions',
          icon: SVGAssets.SubscriptionIcon,
          selectedIcon: SVGAssets.SubscriptionSelected
        },
        !isPermission &&
          isHaveSubscription && {
            label: t('payments'),
            path: '/settings/payments',
            icon: SVGAssets.PaymentsIcon,
            selectedIcon: SVGAssets.PaymentSelected
          },
        {
          label: t('securityPrivacy'),
          path: '/settings/security-privacy',
          icon: SVGAssets.SecurityPrivacy,
          selectedIcon: SVGAssets.SecuritySelected
        },
        {
          label: t('notification'),
          path: '/settings/notification',
          icon: SVGAssets.Notification,
          selectedIcon: SVGAssets.NotificationSelected
        },
        {
          label: t('help'),
          path: '/settings/help',
          icon: SVGAssets.Help,
          selectedIcon: SVGAssets.HelpSelected
        }
      ]
    }
  ];

  // Active check using startsWith
  const isActive = (path?: string) =>
    path && location.pathname.startsWith(path);

  return (
    <>
      {!isMobileOpen && (
        <button
          type="button"
          aria-label="Open navigation"
          onClick={() => setIsMobileOpen(true)}
          className="fixed left-2 top-5 z-[60] inline-flex h-11 w-11 items-center justify-center text-[#49712B] md:hidden">
          <span className="flex flex-col gap-1.5">
            <span className="block h-0.5 w-5 rounded-full bg-current" />
            <span className="block h-0.5 w-5 rounded-full bg-current" />
            <span className="block h-0.5 w-5 rounded-full bg-current" />
          </span>
        </button>
      )}

      {isMobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setIsMobileOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-[60] flex w-64 -translate-x-full flex-col overflow-y-auto border-r border-gray-200 bg-white transition-transform duration-300 ease-out md:static md:z-auto md:translate-x-0 md:flex-shrink-0 ${
          isMobileOpen ? 'translate-x-0 shadow-xl' : ''
        }`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4 md:hidden">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#49712B]">
            {t('menu')}
          </span>
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setIsMobileOpen(false)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#505050] hover:bg-[#ebe5e5]">
            <span className="relative block h-4 w-4">
              <span className="absolute left-1/2 top-1/2 h-0.5 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-current" />
              <span className="absolute left-1/2 top-1/2 h-0.5 w-4 -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-current" />
            </span>
          </button>
        </div>
        <nav className="p-4 space-y-1">
          {sidebarItems
            .filter(
              item => !item.permission || permission.includes(item.permission)
            )
            .map((item, index) => {
              const Icon = item.icon;

              // Parent active when any child starts with path
              const isSettingsActive =
                item.key === 'settings' &&
                item.children
                  ?.filter(Boolean)
                  .some((sub: any) => location.pathname.startsWith(sub.path));

              // Highlight parent or direct item
              const active = isActive(item.path) || isSettingsActive;

              return (
                <div key={index}>
                  <Button
                    btnStyle="link"
                    onClick={() => {
                      if (item.key === 'settings') {
                        const firstChildPath =
                          item.children?.filter(Boolean)?.[0]?.path;
                        const nextOpen = !openSettings;
                        setOpenSettings(nextOpen);
                        if (nextOpen && firstChildPath) {
                          navigate(firstChildPath);
                        }
                      } else if (item.path) {
                        if (item.key === 'dashboard') {
                          resetFilterOptionsForPath();
                        }
                        navigate(item.path);
                      }
                    }}
                    className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors
                  ${active ? 'bg-[#49712B] text-white font-medium' : 'text-[#505050] hover:bg-[#ebe5e5]'}
                  ${item.key === 'dashboard' ? 'mb-10 text-[#505050] font-semibold' : ''}
                `}>
                    <Icon
                      className={`w-5 h-5 ${active ? 'text-white' : 'text-[#505050]'}`}
                    />
                    <span
                      className={`text-sm font-semibold ${active ? 'text-white' : 'text-[#505050]'}`}>
                      {item.label}
                    </span>
                  </Button>

                  {/* SUBMENU */}
                  {item.children && openSettings && (
                    <div className="ml-10 mt-2 space-y-3">
                      {item.children
                        .filter(Boolean)
                        .map((sub: any, idx: number) => {
                          const subActive = location.pathname.startsWith(
                            sub.path
                          );
                          const SubIcon =
                            subActive && sub.selectedIcon
                              ? sub.selectedIcon
                              : sub.icon;

                          return (
                            <a
                              href={sub.path}
                              key={idx}
                              className={`
                          flex items-center gap-3 px-4 py-2 text-xs ml-2 rounded-lg text-[#505050] font-semibold
                          ${subActive ? 'bg-[#ebe5e5]' : ' hover:bg-[#ebe5e5]'}
                        `}>
                              <SubIcon className={`w-5 h-5 text-[#505050]`} />
                              <span>{sub.label}</span>
                            </a>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
        </nav>
      </aside>
    </>
  );
};
